/**
 * Demo Site Data Generator
 *
 * This file generates realistic demo data for the Humblytics demo site.
 * It overrides API responses when viewing the demo property and generates
 * fake data matching the React API response types.
 */

import { DEMO_PROPERTY_ID } from "@/lib/constants";
import type {
  ClicksBreakdownResponse,
  PagesBreakdownResponse,
  PagesDetailsResponse,
  FormsBreakdownResponse,
} from "@/lib/api/external-api";
import type {
  FunnelResponse,
  FunnelSuggestionsResponse,
} from "@/lib/types/funnel-api";

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Check if the current property is the demo site
 */
export function isDemoSite(propertyId?: string | null): boolean {
  return propertyId === DEMO_PROPERTY_ID || false;
}

// Seeded PRNG (mulberry32) — seed is based on the current UTC day so demo data
// is stable within a day but shifts naturally between days.
function makePrng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s += 0x6d2b79f5;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) >>> 0;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const _daySeed = Math.floor(Date.now() / 86400000); // changes once per UTC day
const _rand = makePrng(_daySeed);

/**
 * Generate a random number between min and max
 */
function randomInt(min: number, max: number): number {
  return Math.floor(_rand() * (max - min + 1)) + min;
}

/**
 * Generate a random float between min and max
 */
function randomFloat(min: number, max: number, decimals: number = 2): number {
  return parseFloat((_rand() * (max - min) + min).toFixed(decimals));
}

/**
 * Generate ISO timestamp for N days ago
 */
function daysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString();
}

/**
 * Generate a date range array
 */
function generateDateRange(days: number): string[] {
  const dates: string[] = [];
  for (let i = days; i >= 0; i--) {
    dates.push(daysAgo(i));
  }
  return dates;
}

/**
 * Generate realistic trend data with slight variations
 */
function generateTrendData(
  baseValue: number,
  buckets: number,
  variance: number = 0.2
): number[] {
  const data: number[] = [];
  let current = baseValue;

  for (let i = 0; i < buckets; i++) {
    const change = (_rand() - 0.5) * 2 * variance * baseValue;
    current = Math.max(0, current + change);
    data.push(Math.round(current));
  }

  return data;
}

// ============================================================================
// DEMO DATA GENERATORS
// ============================================================================

/**
 * Generate demo dashboard stats (main analytics overview)
 */
function generateDemoDashboardStats() {
  const baselineVisitors = 1250;
  const baselinePageviews = 3800;
  const dates = generateDateRange(30);

  return {
    metrics: {
      // Domain for building full URLs
      domain: "https://humblytics.com",

      // Trends data for charts (expected by Traffic page)
      trends: {
        sessions: dates.map((date) => ({
          date,
          count: String(generateTrendData(baselineVisitors, 1)[0]),
        })),
        views: dates.map((date) => ({
          date,
          count: String(generateTrendData(baselinePageviews, 1)[0]),
        })),
        // Total sessions for visitor count display
        totalSessions: [
          {
            total_sessions: 12847,
            unique_visitors: 12847,
          },
        ],
        // Bounce data for bounce rate
        bounces: [
          {
            bounce_count: 5438,
          },
        ],
        singlePageSessions: [
          {
            bounce_count: 5438,
          },
        ],
        // Session time data for duration display
        sessionTime: dates.map((date) => ({
          date,
          total_session_time: String(randomInt(180000, 320000)), // milliseconds
        })),
        // Revenue data (optional, but included for completeness)
        revenue: dates.map((date) => ({
          date,
          total_revenue: String(randomInt(0, 0)), // $0 for demo
        })),
      },

      // Aggregate data for devices/browsers/OS/referrers
      aggregate: [
        // OS entries with client_os_aggregate flag
        {
          client_os: "Windows",
          client_os_aggregate: "client_os",
          count: "4850",
        },
        {
          client_os: "Mac OS",
          client_os_aggregate: "client_os",
          count: "3240",
        },
        { client_os: "iOS", client_os_aggregate: "client_os", count: "2180" },
        {
          client_os: "Android",
          client_os_aggregate: "client_os",
          count: "1840",
        },
        { client_os: "Linux", client_os_aggregate: "client_os", count: "680" },
        // Browser entries with client_browser_aggregate flag
        {
          client_browser: "Chrome",
          client_browser_aggregate: "client_browser",
          count: "6890",
        },
        {
          client_browser: "Safari",
          client_browser_aggregate: "client_browser",
          count: "3240",
        },
        {
          client_browser: "Firefox",
          client_browser_aggregate: "client_browser",
          count: "1560",
        },
        {
          client_browser: "Edge",
          client_browser_aggregate: "client_browser",
          count: "890",
        },
        {
          client_browser: "Other",
          client_browser_aggregate: "client_browser",
          count: "267",
        },
        // Referrer group entries with referer_group_aggregate flag
        {
          referer_group: "google.com",
          referer_group_aggregate: "referer_group",
          utm_source: "google",
          utm_medium: "organic",
          count: "4520",
        },
        {
          referer_group: "(direct)",
          referer_group_aggregate: "referer_group",
          count: "3180",
        },
        {
          referer_group: "linkedin.com",
          referer_group_aggregate: "referer_group",
          utm_source: "linkedin",
          utm_medium: "social",
          count: "1850",
        },
        {
          referer_group: "t.co",
          referer_group_aggregate: "referer_group",
          utm_source: "twitter",
          utm_medium: "social",
          count: "980",
        },
        {
          referer_group: "youtube.com",
          referer_group_aggregate: "referer_group",
          utm_source: "youtube",
          utm_medium: "referral",
          count: "620",
        },
        {
          referer_group: "github.com",
          referer_group_aggregate: "referer_group",
          utm_source: "github",
          utm_medium: "referral",
          count: "450",
        },
        // Country entries
        { country: "US", country_code: "US", count: "5420" },
        { country: "GB", country_code: "GB", count: "2180" },
        { country: "CA", country_code: "CA", count: "1340" },
        { country: "DE", country_code: "DE", count: "980" },
        { country: "AU", country_code: "AU", count: "780" },
        { country: "FR", country_code: "FR", count: "620" },
      ],

      // Pages data nested under 'pages'
      pages: {
        pageSessions: [
          { page: "/", count: "7850", unique_visitors: 5840 },
          { page: "/pricing", count: "8420", unique_visitors: 6210 },
          { page: "/features", count: "6230", unique_visitors: 4580 },
          {
            page: "/blog/ab-testing-guide",
            count: "4120",
            unique_visitors: 3890,
          },
          { page: "/contact", count: "2340", unique_visitors: 2180 },
        ],
      },

      // Entry pages
      entryPages: [
        { page: "/", entry_count: "4520", bounce_rate: 38.4 },
        { page: "/pricing", entry_count: "2840", bounce_rate: 32.1 },
        {
          page: "/blog/ab-testing-guide",
          entry_count: "1680",
          bounce_rate: 28.7,
        },
        { page: "/features", entry_count: "1230", bounce_rate: 41.2 },
      ],

      // Exit pages
      exitPages: [
        { page: "/pricing", entry_count: "3240", exit_rate: 42.3 },
        { page: "/contact", entry_count: "1890", exit_rate: 78.2 },
        { page: "/", entry_count: "1560", exit_rate: 19.8 },
        { page: "/blog/ab-testing-guide", entry_count: "980", exit_rate: 23.7 },
      ],

      // Referrals (kept for backward compatibility)
      referrals: [
        { referer_group: "google.com", count: "4520", unique_visitors: 3840 },
        { referer_group: "Direct", count: "3180", unique_visitors: 2920 },
        { referer_group: "linkedin.com", count: "1850", unique_visitors: 1680 },
        { referer_group: "t.co", count: "980", unique_visitors: 890 },
        { referer_group: "youtube.com", count: "620", unique_visitors: 580 },
      ],

      // UTM Results with aggregate flags for ReferralComponent
      utmResults: [
        // Source aggregates
        {
          utm_source: "google",
          utm_source_aggregate: "utm_source",
          count: "4520",
        },
        {
          utm_source: "linkedin",
          utm_source_aggregate: "utm_source",
          count: "1850",
        },
        {
          utm_source: "twitter",
          utm_source_aggregate: "utm_source",
          count: "980",
        },
        {
          utm_source: "mailchimp",
          utm_source_aggregate: "utm_source",
          count: "890",
        },
        // Medium aggregates
        {
          utm_medium: "organic",
          utm_medium_aggregate: "utm_medium",
          count: "4520",
        },
        {
          utm_medium: "social",
          utm_medium_aggregate: "utm_medium",
          count: "2830",
        },
        {
          utm_medium: "cpc",
          utm_medium_aggregate: "utm_medium",
          count: "2340",
        },
        {
          utm_medium: "email",
          utm_medium_aggregate: "utm_medium",
          count: "890",
        },
        // Campaign aggregates
        {
          utm_campaign: "brand_search",
          utm_campaign_aggregate: "utm_campaign",
          utm_source: "google",
          utm_medium: "cpc",
          count: "2340",
        },
        {
          utm_campaign: "content_marketing",
          utm_campaign_aggregate: "utm_campaign",
          utm_source: "linkedin",
          utm_medium: "social",
          count: "1560",
        },
        {
          utm_campaign: "newsletter_weekly",
          utm_campaign_aggregate: "utm_campaign",
          utm_source: "mailchimp",
          utm_medium: "email",
          count: "890",
        },
        {
          utm_campaign: "launch_week",
          utm_campaign_aggregate: "utm_campaign",
          utm_source: "twitter",
          utm_medium: "social",
          count: "670",
        },
        // Content aggregates (optional)
        {
          utm_content: "hero_cta",
          utm_content_aggregate: "utm_content",
          count: "1240",
        },
        {
          utm_content: "sidebar_banner",
          utm_content_aggregate: "utm_content",
          count: "890",
        },
      ],
    },
    stats: {
      // Main metrics
      currentVisitors: randomInt(15, 45),
      totalVisitors: 12847,
      totalPageviews: 45621,
      avgSessionDuration: 245, // seconds
      bounceRate: 42.3,
      totalSessions: 18450,

      // Comparison with previous period
      visitorsChange: randomFloat(-15, 25, 1),
      pageviewsChange: randomFloat(-10, 30, 1),
      sessionDurationChange: randomFloat(-20, 15, 1),
      bounceRateChange: randomFloat(-10, 5, 1),

      // Time series data
      visitorsTimeSeries: dates.map((date, i) => ({
        date,
        visitors: generateTrendData(baselineVisitors, 1)[0],
        pageviews: generateTrendData(baselinePageviews, 1)[0],
      })),

      // Top pages
      pages: [
        {
          page: "/pricing",
          pageviews: 8420,
          uniqueVisitors: 6210,
          bounceRate: 38.2,
        },
        { page: "/", pageviews: 7850, uniqueVisitors: 5840, bounceRate: 45.1 },
        {
          page: "/features",
          pageviews: 6230,
          uniqueVisitors: 4580,
          bounceRate: 41.5,
        },
        {
          page: "/blog/ab-testing-guide",
          pageviews: 4120,
          uniqueVisitors: 3890,
          bounceRate: 32.8,
        },
        {
          page: "/contact",
          pageviews: 2340,
          uniqueVisitors: 2180,
          bounceRate: 28.4,
        },
        {
          page: "/about",
          pageviews: 1890,
          uniqueVisitors: 1620,
          bounceRate: 51.2,
        },
        {
          page: "/docs",
          pageviews: 1560,
          uniqueVisitors: 1240,
          bounceRate: 36.7,
        },
        {
          page: "/case-studies",
          pageviews: 980,
          uniqueVisitors: 870,
          bounceRate: 29.3,
        },
      ],

      // Referrers
      referrals: [
        { referer_group: "google.com", count: "4520", unique_visitors: 3840 },
        { referer_group: "Direct", count: "3180", unique_visitors: 2920 },
        { referer_group: "linkedin.com", count: "1850", unique_visitors: 1680 },
        { referer_group: "t.co", count: "980", unique_visitors: 890 },
        { referer_group: "youtube.com", count: "620", unique_visitors: 580 },
        { referer_group: "github.com", count: "450", unique_visitors: 410 },
        {
          referer_group: "producthunt.com",
          count: "380",
          unique_visitors: 350,
        },
      ],

      // UTM tracking
      utmResults: [
        {
          utm_source: "google",
          utm_campaign: "brand_search",
          utm_medium: "cpc",
          count: "2340",
        },
        {
          utm_source: "linkedin",
          utm_campaign: "content_marketing",
          utm_medium: "social",
          count: "1560",
        },
        {
          utm_source: "mailchimp",
          utm_campaign: "newsletter_weekly",
          utm_medium: "email",
          count: "890",
        },
        {
          utm_source: "twitter",
          utm_campaign: "launch_week",
          utm_medium: "social",
          count: "670",
        },
      ],

      // Devices
      devices: [
        { device_type: "Desktop", visitors: 7840, share: 61.0 },
        { device_type: "Mobile", visitors: 4120, share: 32.1 },
        { device_type: "Tablet", visitors: 887, share: 6.9 },
      ],

      // Browsers
      browsers: [
        { browser: "Chrome", visitors: 6890, share: 53.6 },
        { browser: "Safari", visitors: 3240, share: 25.2 },
        { browser: "Firefox", visitors: 1560, share: 12.1 },
        { browser: "Edge", visitors: 890, share: 6.9 },
        { browser: "Other", visitors: 267, share: 2.2 },
      ],

      // Countries/Locations
      locations: [
        {
          country_code: "US",
          country: "🇺🇸 United States",
          visitors: 5420,
          share: 42.2,
        },
        {
          country_code: "GB",
          country: "🇬🇧 United Kingdom",
          visitors: 2180,
          share: 17.0,
        },
        {
          country_code: "CA",
          country: "🇨🇦 Canada",
          visitors: 1340,
          share: 10.4,
        },
        {
          country_code: "DE",
          country: "🇩🇪 Germany",
          visitors: 980,
          share: 7.6,
        },
        {
          country_code: "AU",
          country: "🇦🇺 Australia",
          visitors: 780,
          share: 6.1,
        },
        { country_code: "FR", country: "🇫🇷 France", visitors: 620, share: 4.8 },
        {
          country_code: "NL",
          country: "🇳🇱 Netherlands",
          visitors: 450,
          share: 3.5,
        },
        { country_code: "IN", country: "🇮🇳 India", visitors: 390, share: 3.0 },
        { country_code: "ES", country: "🇪🇸 Spain", visitors: 320, share: 2.5 },
        { country_code: "Other", country: "Other", visitors: 367, share: 2.9 },
      ],

      // Entry & Exit pages
      entryPages: [
        { page: "/", entry_count: "4520", bounce_rate: 38.4 },
        { page: "/pricing", entry_count: "2840", bounce_rate: 32.1 },
        {
          page: "/blog/ab-testing-guide",
          entry_count: "1680",
          bounce_rate: 28.7,
        },
        { page: "/features", entry_count: "1230", bounce_rate: 41.2 },
      ],

      exitPages: [
        { page: "/pricing", entry_count: "3240", exit_rate: 42.3 },
        { page: "/contact", entry_count: "1890", exit_rate: 78.2 },
        { page: "/", entry_count: "1560", exit_rate: 19.8 },
        { page: "/blog/ab-testing-guide", entry_count: "980", exit_rate: 23.7 },
      ],
    },
  };
}

/**
 * Generate demo clicks data matching ClicksBreakdownResponse
 */
function generateDemoClicksData(
  granularityPeriod: string = "day_PAST_7_D"
): ClicksBreakdownResponse {
  const now = new Date();
  const startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  return {
    meta: {
      property_id: DEMO_PROPERTY_ID,
      start: startDate.toISOString(),
      end: now.toISOString(),
      timezone: "America/Los_Angeles",
      granularity: "day",
      limit: 100,
      bucket_count: 7,
    },
    data: [
      {
        page: "/pricing",
        page_group: "/pricing",
        total_clicks: 1840,
        unique_sessions: 1520,
        top_targets: [
          { target: "button[Start free trial]", clicks: 620, share: 33.7 },
          { target: "a[Contact Sales]", clicks: 450, share: 24.5 },
          { target: "button[See Features]", clicks: 380, share: 20.7 },
          { target: "a[FAQ]", clicks: 230, share: 12.5 },
          { target: "a[Compare Plans]", clicks: 160, share: 8.6 },
        ],
      },
      {
        page: "/",
        page_group: "/",
        total_clicks: 2340,
        unique_sessions: 1980,
        top_targets: [
          { target: "button[Get started]", clicks: 890, share: 38.0 },
          { target: "a[Learn more]", clicks: 560, share: 23.9 },
          { target: "a[View Demo]", clicks: 450, share: 19.2 },
          { target: "nav a[Pricing]", clicks: 280, share: 12.0 },
          { target: "footer a[Contact]", clicks: 160, share: 6.9 },
        ],
      },
      {
        page: "/features",
        page_group: "/features",
        total_clicks: 1560,
        unique_sessions: 1320,
        top_targets: [
          { target: "button[Try Feature]", clicks: 520, share: 33.3 },
          { target: "a[Documentation]", clicks: 380, share: 24.4 },
          { target: "video[play]", clicks: 340, share: 21.8 },
          { target: "a[Case Studies]", clicks: 210, share: 13.5 },
          { target: "button[Request Demo]", clicks: 110, share: 7.0 },
        ],
      },
    ],
    trend: generateDateRange(7).map((date) => ({
      bucket_start: date,
      clicks: randomInt(800, 1400),
      unique_sessions: randomInt(650, 1150),
    })),
    summary: {
      total_clicks: 8940,
      unique_sessions: 7320,
      pages_with_clicks: 45,
      groups: {
        "/pricing": { pages: 1, total_clicks: 1840, unique_sessions: 1520 },
        "/": { pages: 1, total_clicks: 2340, unique_sessions: 1980 },
        "/features": { pages: 3, total_clicks: 1560, unique_sessions: 1320 },
        "/blog": { pages: 12, total_clicks: 2120, unique_sessions: 1680 },
        "/docs": { pages: 8, total_clicks: 1080, unique_sessions: 820 },
      },
    },
  };
}

/**
 * Generate demo pages data matching PagesBreakdownResponse
 */
function generateDemoPagesData(
  granularityPeriod: string = "day_PAST_7_D"
): PagesBreakdownResponse {
  const now = new Date();
  const startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  return {
    meta: {
      property_id: DEMO_PROPERTY_ID,
      start: startDate.toISOString(),
      end: now.toISOString(),
      timezone: "America/Los_Angeles",
      granularity: "day",
      limit: 100,
      bucket_count: 7,
    },
    data: [
      {
        page: "/",
        page_group: "/",
        page_views: 4520,
        unique_visitors: 3680,
        avg_session_length: 145.5,
        avg_scroll_percent: 65.3,
        bounce_rate: 0.42,
        top_sources: [
          {
            utm_source: "google",
            utm_medium: "organic",
            sessions: 1840,
            share: 0.5,
          },
          { utm_source: "direct", utm_medium: "", sessions: 920, share: 0.25 },
          {
            utm_source: "twitter",
            utm_medium: "social",
            sessions: 552,
            share: 0.15,
          },
          {
            utm_source: "linkedin",
            utm_medium: "social",
            sessions: 368,
            share: 0.1,
          },
        ],
      },
      {
        page: "/pricing",
        page_group: "/pricing",
        page_views: 3240,
        unique_visitors: 2890,
        avg_session_length: 210.3,
        avg_scroll_percent: 78.2,
        bounce_rate: 0.35,
        top_sources: [
          {
            utm_source: "google",
            utm_medium: "cpc",
            sessions: 1296,
            share: 0.45,
          },
          {
            utm_source: "google",
            utm_medium: "organic",
            sessions: 972,
            share: 0.34,
          },
          { utm_source: "direct", utm_medium: "", sessions: 648, share: 0.21 },
        ],
      },
      {
        page: "/features",
        page_group: "/features",
        page_views: 2680,
        unique_visitors: 2140,
        avg_session_length: 178.6,
        avg_scroll_percent: 71.5,
        bounce_rate: 0.38,
        top_sources: [
          {
            utm_source: "google",
            utm_medium: "organic",
            sessions: 1072,
            share: 0.5,
          },
          {
            utm_source: "producthunt",
            utm_medium: "referral",
            sessions: 536,
            share: 0.25,
          },
          {
            utm_source: "twitter",
            utm_medium: "social",
            sessions: 428,
            share: 0.2,
          },
        ],
      },
      {
        page: "/blog",
        page_group: "/blog",
        page_views: 1840,
        unique_visitors: 1560,
        avg_session_length: 195.4,
        avg_scroll_percent: 82.7,
        bounce_rate: 0.51,
        top_sources: [
          {
            utm_source: "google",
            utm_medium: "organic",
            sessions: 1288,
            share: 0.7,
          },
          {
            utm_source: "twitter",
            utm_medium: "social",
            sessions: 368,
            share: 0.2,
          },
          {
            utm_source: "reddit",
            utm_medium: "social",
            sessions: 184,
            share: 0.1,
          },
        ],
      },
      {
        page: "/blog/how-to-measure-ctr",
        page_group: "/blog",
        page_views: 920,
        unique_visitors: 780,
        avg_session_length: 210.2,
        avg_scroll_percent: 76.4,
        bounce_rate: 0.48,
        top_sources: [
          {
            utm_source: "google",
            utm_medium: "organic",
            sessions: 640,
            share: 0.82,
          },
          {
            utm_source: "twitter",
            utm_medium: "social",
            sessions: 140,
            share: 0.18,
          },
        ],
      },
      {
        page: "/templates/landing-page",
        page_group: "/templates",
        page_views: 640,
        unique_visitors: 520,
        avg_session_length: 168.0,
        avg_scroll_percent: 71.2,
        bounce_rate: 0.44,
        top_sources: [
          {
            utm_source: "google",
            utm_medium: "organic",
            sessions: 410,
            share: 0.79,
          },
          { utm_source: "direct", utm_medium: "", sessions: 110, share: 0.21 },
        ],
      },
      {
        page: "/docs",
        page_group: "/docs",
        page_views: 1520,
        unique_visitors: 980,
        avg_session_length: 325.8,
        avg_scroll_percent: 88.4,
        bounce_rate: 0.28,
        top_sources: [
          {
            utm_source: "google",
            utm_medium: "organic",
            sessions: 686,
            share: 0.7,
          },
          { utm_source: "direct", utm_medium: "", sessions: 196, share: 0.2 },
          {
            utm_source: "github",
            utm_medium: "referral",
            sessions: 98,
            share: 0.1,
          },
        ],
      },
    ],
    trend: generateDateRange(7).map((date) => ({
      bucket_start: date,
      page_views: randomInt(1800, 2800),
      unique_visitors: randomInt(1200, 2000),
    })),
    summary: {
      total_pages: 45,
      total_page_views: 15360,
      total_unique_visitors: 12550,
      groups: {
        "/": { pages: 1, page_views: 4520, unique_visitors: 3680 },
        "/pricing": { pages: 1, page_views: 3240, unique_visitors: 2890 },
        "/features": { pages: 3, page_views: 2680, unique_visitors: 2140 },
        "/blog": { pages: 12, page_views: 2760, unique_visitors: 2340 },
        "/templates": { pages: 4, page_views: 640, unique_visitors: 520 },
        "/docs": { pages: 8, page_views: 1520, unique_visitors: 980 },
      },
    },
  };
}

/**
 * Generate demo forms data matching FormsBreakdownResponse
 */
function generateDemoFormsData(
  granularityPeriod: string = "day_PAST_7_D"
): FormsBreakdownResponse {
  const now = new Date();
  const startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  return {
    meta: {
      property_id: DEMO_PROPERTY_ID,
      start: startDate.toISOString(),
      end: now.toISOString(),
      timezone: "America/Los_Angeles",
      granularity: "day",
      limit: 100,
      bucket_count: 7,
    },
    data: [
      {
        page: "/contact",
        page_group: "/contact",
        total_submissions: 245,
        unique_submissions: 223,
        unique_sessions: 223,
        top_forms: [
          {
            target: "form#contact_form_main",
            submissions: 245,
            share: 100.0,
          },
        ],
      },
      {
        page: "/",
        page_group: "/",
        total_submissions: 489,
        unique_submissions: 456,
        unique_sessions: 456,
        top_forms: [
          {
            target: "form#newsletter_signup",
            submissions: 489,
            share: 100.0,
          },
        ],
      },
      {
        page: "/pricing",
        page_group: "/pricing",
        total_submissions: 178,
        unique_submissions: 167,
        unique_sessions: 167,
        top_forms: [
          {
            target: "form#trial_signup_form",
            submissions: 178,
            share: 100.0,
          },
        ],
      },
    ],
    trend: generateDateRange(7).map((date) => {
      const unique = randomInt(98, 165)
      return {
        bucket_start: date,
        submissions: randomInt(110, 180),
        unique_submissions: unique,
        unique_sessions: unique,
      }
    }),
    summary: {
      total_submissions: 912,
      unique_submissions: 846,
      total_unique_submissions: 846,
      unique_sessions: 846,
      pages_with_forms: 3,
      groups: {
        "/contact": { pages: 1, total_submissions: 245, unique_submissions: 223, unique_sessions: 223 },
        "/": { pages: 1, total_submissions: 489, unique_submissions: 456, unique_sessions: 456 },
        "/pricing": { pages: 1, total_submissions: 178, unique_submissions: 167, unique_sessions: 167 },
      },
    },
  };
}

/**
 * Generate demo experiments (A/B tests) data
 * Returns data in the format expected by the useExperiments hook
 * API format: array of { experimentId, experiment: {...}, status, variants: { [id]: stats }, ... }
 */
function generateDemoExperiments() {
  return [
    {
      experimentId: "exp_hero_cta_test",
      experiment: {
        experimentName: "Homepage Hero CTA Test",
        testGoal: "click_through_rate",
        variants: [
          {
            id: "control",
            url: "/",
            label: 'Control - "Start free trial"',
            notes: "Original CTA button",
            isControl: true,
            screenshots: [],
          },
          {
            id: "variant_a",
            url: "/",
            label: 'Variant A - "Get started Free"',
            notes: "Updated CTA copy",
            isControl: false,
            screenshots: [],
          },
        ],
        isUserLevel: false,
        finishPageUrl: "/signup",
      },
      experimentType: "redirect",
      status: "active",
      createdAt: daysAgo(14),
      endedAt: null,
      updatedAt: daysAgo(1),
      // Variant metrics (keyed by variant ID)
      variants: {
        control: {
          sessions: 2840,
          conversions: 342,
          conversionRate: 12.04,
          bounceRate: 38.2,
          avgTimeOnPage: 45,
        },
        variant_a: {
          sessions: 2910,
          conversions: 436,
          conversionRate: 14.98,
          bounceRate: 35.1,
          avgTimeOnPage: 52,
        },
      },
    },
    {
      experimentId: "exp_pricing_layout",
      experiment: {
        experimentName: "Pricing Page Layout",
        testGoal: "page_funnel",
        variants: [
          {
            id: "control",
            url: "/pricing",
            label: "Control - 3 Column Layout",
            notes: "Standard pricing grid",
            isControl: true,
            screenshots: [],
          },
          {
            id: "variant_b",
            url: "/pricing-v2",
            label: "Variant B - Comparison Table",
            notes: "Feature comparison layout",
            isControl: false,
            screenshots: [],
          },
        ],
        isUserLevel: false,
        finishPageUrl: "/checkout",
      },
      experimentType: "redirect",
      status: "active",
      createdAt: daysAgo(21),
      endedAt: null,
      updatedAt: daysAgo(2),
      variants: {
        control: {
          sessions: 3620,
          conversions: 289,
          conversionRate: 7.98,
          bounceRate: 42.5,
          avgTimeOnPage: 68,
        },
        variant_b: {
          sessions: 3580,
          conversions: 318,
          conversionRate: 8.88,
          bounceRate: 39.8,
          avgTimeOnPage: 74,
        },
      },
    },
    {
      experimentId: "exp_mobile_nav",
      experiment: {
        experimentName: "Mobile Navigation Style",
        testGoal: "click_through_rate",
        variants: [
          {
            id: "control",
            url: "/",
            label: "Control - Hamburger Menu",
            notes: "Standard hamburger nav",
            isControl: true,
            screenshots: [],
          },
          {
            id: "variant_c",
            url: "/",
            label: "Variant C - Bottom Tab Bar",
            notes: "iOS-style bottom navigation",
            isControl: false,
            screenshots: [],
          },
        ],
        isUserLevel: false,
        finishPageUrl: "/signup",
      },
      experimentType: "component_visual",
      status: "complete",
      createdAt: daysAgo(45),
      endedAt: daysAgo(28),
      updatedAt: daysAgo(28),
      variants: {
        control: {
          sessions: 4520,
          conversions: 498,
          conversionRate: 11.02,
          bounceRate: 45.3,
          avgTimeOnPage: 42,
        },
        variant_c: {
          sessions: 4480,
          conversions: 627,
          conversionRate: 14.0,
          bounceRate: 38.7,
          avgTimeOnPage: 56,
        },
      },
    },
  ];
}

/**
 * Generate demo saved funnels list.
 * Returns the shape the `metrics/:propertyId/funnels` endpoint produces:
 * { funnels: [ { funnel_id, name, description, steps: [{ id, name, url, type, ... }] } ] }
 */
function generateDemoFunnels() {
  return {
    funnels: [
      {
        funnel_id: "funnel_signup",
        name: "Signup Funnel",
        description: "Track visitors from homepage through registration",
        created_at: daysAgo(45),
        property_id: DEMO_PROPERTY_ID,
        steps: [
          { id: "1", name: "Homepage", url: "/", type: "page_view" },
          { id: "2", name: "Pricing Page", url: "/pricing", type: "page_view" },
          {
            id: "3",
            name: "Get started Click",
            url: "Get started",
            type: "click",
            secondary: "button",
          },
          {
            id: "4",
            name: "Signup Form",
            url: "signup-form",
            type: "form_submission",
          },
        ],
      },
      {
        funnel_id: "funnel_checkout",
        name: "Checkout Funnel",
        description: "Track visitors from product page to purchase completion",
        created_at: daysAgo(60),
        property_id: DEMO_PROPERTY_ID,
        steps: [
          { id: "1", name: "Product Page", url: "/product/*", type: "page_view" },
          {
            id: "2",
            name: "Add to Cart",
            url: "Add to Cart",
            type: "click",
            secondary: "button",
          },
          { id: "3", name: "Checkout", url: "/checkout", type: "page_view" },
          {
            id: "4",
            name: "Order Confirmation",
            url: "/order-confirmation",
            type: "page_view",
          },
        ],
      },
      {
        funnel_id: "funnel_blog_to_trial",
        name: "Blog → Trial Funnel",
        description: "Content-led visitors converting to free trial signups",
        created_at: daysAgo(30),
        property_id: DEMO_PROPERTY_ID,
        steps: [
          { id: "1", name: "Blog Post", url: "/blog/*", type: "page_view" },
          { id: "2", name: "Features Page", url: "/features", type: "page_view" },
          {
            id: "3",
            name: "Start Trial Click",
            url: "Start free trial",
            type: "click",
            secondary: "button",
          },
          {
            id: "4",
            name: "Trial Signup Form",
            url: "trial-signup-form",
            type: "form_submission",
          },
        ],
      },
    ],
  };
}

/**
 * Generate a demo funnel analysis result (`FunnelResponse` shape).
 * `stepNames` — ordered list of step names from the request; defaults to a
 * plausible 4-step signup funnel if omitted.
 */
function generateDemoFunnelAnalysis(
  stepNames?: string[]
): FunnelResponse {
  const now = new Date();
  const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

  const names = stepNames?.length
    ? stepNames
    : ["Homepage", "Pricing Page", "Get started Click", "Signup Form"];

  // Realistic conversion curve: each step keeps 55–80 % of the previous
  const totalEntered = 12847;
  const retentionRates = [1.0, 0.352, 0.221, 0.191]; // absolute rates from entry

  const steps = names.map((name, i) => {
    const rate =
      i < retentionRates.length
        ? retentionRates[i]
        : retentionRates[retentionRates.length - 1] * Math.pow(0.65, i - (retentionRates.length - 1));
    const sessions = Math.round(totalEntered * rate);
    const prevRate =
      i === 0
        ? 1.0
        : i < retentionRates.length
        ? retentionRates[i - 1]
        : retentionRates[retentionRates.length - 1] * Math.pow(0.65, i - retentionRates.length);
    const prevSessions = Math.round(totalEntered * prevRate);
    const dropOff = prevSessions - sessions;
    const isFormStep = name.toLowerCase().includes("form");
    return {
      step_index: i + 1,
      step_name: name,
      total_sessions: sessions,
      // Form steps report slightly more raw events than unique sessions
      total_events: isFormStep ? Math.round(sessions * 1.08) : sessions,
      drop_off_count: dropOff,
      drop_off_rate: i === 0 ? 0 : dropOff / prevSessions,
      conversion_rate: rate,
      avg_time_from_previous: i === 0 ? null : randomInt(30, 300),
      median_time_from_previous: i === 0 ? null : randomInt(20, 180),
    };
  });

  const totalCompleted = steps[steps.length - 1].total_sessions;

  return {
    meta: {
      property_id: DEMO_PROPERTY_ID,
      start: start.toISOString(),
      end: now.toISOString(),
      timezone: "America/Los_Angeles",
      mode: "sequential",
      step_count: steps.length,
    },
    data: { steps },
    summary: {
      total_entered: totalEntered,
      total_completed: totalCompleted,
      overall_conversion_rate: totalCompleted / totalEntered,
      avg_completion_time: randomInt(180, 540),
    },
  };
}

/**
 * Generate demo funnel step suggestions (`FunnelSuggestionsResponse` shape).
 * Used by the FunnelStudio step builder autocomplete.
 */
function generateDemoFunnelSuggestions(): FunnelSuggestionsResponse {
  const now = new Date();
  const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

  return {
    meta: {
      property_id: DEMO_PROPERTY_ID,
      start: start.toISOString(),
      end: now.toISOString(),
    },
    data: {
      pages: [
        { path: "/", page_views: 12847, unique_sessions: 10234 },
        { path: "/pricing", page_views: 8420, unique_sessions: 6820 },
        { path: "/features", page_views: 6230, unique_sessions: 4980 },
        { path: "/signup", page_views: 4520, unique_sessions: 4200 },
        { path: "/blog", page_views: 4120, unique_sessions: 3450 },
        { path: "/blog/ab-testing-guide", page_views: 2680, unique_sessions: 2450 },
        { path: "/checkout", page_views: 2840, unique_sessions: 2560 },
        { path: "/contact", page_views: 2340, unique_sessions: 1920 },
        { path: "/about", page_views: 1890, unique_sessions: 1540 },
        { path: "/docs", page_views: 1560, unique_sessions: 1240 },
        { path: "/product/*", page_views: 5840, unique_sessions: 5120 },
        { path: "/order-confirmation", page_views: 892, unique_sessions: 850 },
      ],
      clicks: [
        {
          target: "Get started",
          secondary: "button",
          event_count: 3240,
          unique_sessions: 2890,
        },
        {
          target: "Start free trial",
          secondary: "button",
          event_count: 2180,
          unique_sessions: 1940,
        },
        {
          target: "Add to Cart",
          secondary: "button",
          event_count: 3920,
          unique_sessions: 3640,
        },
        {
          target: "Checkout Now",
          secondary: "button",
          event_count: 2340,
          unique_sessions: 2180,
        },
        {
          target: "View Pricing",
          secondary: "link",
          event_count: 1560,
          unique_sessions: 1380,
        },
        {
          target: "Learn more",
          secondary: "link",
          event_count: 1240,
          unique_sessions: 1120,
        },
        {
          target: "Contact Sales",
          secondary: "button",
          event_count: 980,
          unique_sessions: 870,
        },
        {
          target: "Book a Demo",
          secondary: "button",
          event_count: 620,
          unique_sessions: 580,
        },
      ],
      form_submissions: [
        {
          target: "signup-form",
          secondary: null,
          event_count: 2650,
          unique_sessions: 2456,
        },
        {
          target: "trial-signup-form",
          secondary: null,
          event_count: 892,
          unique_sessions: 850,
        },
        {
          target: "contact-form",
          secondary: null,
          event_count: 489,
          unique_sessions: 456,
        },
        {
          target: "newsletter-signup",
          secondary: null,
          event_count: 912,
          unique_sessions: 846,
        },
        {
          target: "demo-request",
          secondary: null,
          event_count: 178,
          unique_sessions: 167,
        },
      ],
      revenue: {
        event_count: 892,
        unique_sessions: 850,
        total_revenue: 44600,
      },
    },
  };
}

/**
 * Generate demo heatmap data
 */
function generateDemoHeatmaps() {
  return {
    heatmaps: [
      {
        id: "heatmap_homepage",
        name: "Homepage Heatmap",
        page: "/",
        property_id: DEMO_PROPERTY_ID,
        created_at: daysAgo(7),
        total_clicks: 8940,
        total_sessions: 5840,
        device_breakdown: {
          desktop: { clicks: 5420, sessions: 3620 },
          mobile: { clicks: 2840, sessions: 1890 },
          tablet: { clicks: 680, sessions: 330 },
        },
        // Click coordinates would be generated here for actual heatmap rendering
        click_data: [],
        scroll_depth: {
          avg_scroll_percentage: 68.4,
          reached_75: 52.3,
          reached_90: 23.8,
          reached_100: 12.1,
        },
      },
      {
        id: "heatmap_pricing",
        name: "Pricing Page Heatmap",
        page: "/pricing",
        property_id: DEMO_PROPERTY_ID,
        created_at: daysAgo(7),
        total_clicks: 6230,
        total_sessions: 4120,
        device_breakdown: {
          desktop: { clicks: 4340, sessions: 2840 },
          mobile: { clicks: 1560, sessions: 1080 },
          tablet: { clicks: 330, sessions: 200 },
        },
        click_data: [],
        scroll_depth: {
          avg_scroll_percentage: 82.7,
          reached_75: 71.4,
          reached_90: 45.2,
          reached_100: 28.6,
        },
      },
    ],
  };
}

// ============================================================================
// DEMO REALTIME DATA - Cached for performance
// ============================================================================

// Cache for realtime events - uses stable IDs that persist between refreshes
let cachedRealtimeEvents: any[] = [];
let lastRealtimeGeneration = 0;
let eventIdCounter = 0; // Monotonically increasing counter for unique stable IDs
let lastNewEventTime = 0; // Track when we last added a new event

const MAX_REALTIME_EVENTS = 25; // Cap total events
const MIN_TIME_BETWEEN_NEW_EVENTS = 8000; // Only add new event every 8+ seconds (not every 5s refresh)
const MAX_NEW_EVENTS_PER_INTERVAL = 2; // Max 2 new events when we do add

// Countries with their typical visitor patterns
const DEMO_COUNTRIES = [
  "US",
  "GB",
  "CA",
  "DE",
  "FR",
  "AU",
  "JP",
  "BR",
  "IN",
  "NL",
  "SE",
  "ES",
  "IT",
  "CH",
];

// Realistic pages
const DEMO_PAGES = [
  "/",
  "/pricing",
  "/features",
  "/about",
  "/blog",
  "/blog/getting-started",
  "/blog/analytics-guide",
  "/docs",
  "/docs/installation",
  "/contact",
  "/demo",
  "/integrations",
];

// Operating systems and browsers
const DEMO_BROWSERS = [
  { browser: "Chrome", os: "Windows" },
  { browser: "Chrome", os: "macOS" },
  { browser: "Safari", os: "macOS" },
  { browser: "Firefox", os: "Windows" },
  { browser: "Edge", os: "Windows" },
  { browser: "Safari", os: "iOS" },
  { browser: "Chrome", os: "Android" },
];

const DEMO_DEVICE_TYPES = ["Desktop", "Mobile", "Tablet"];
const DEMO_UTM_SOURCES = [
  "google",
  "linkedin",
  "twitter",
  "facebook",
  "direct",
  "email",
];
const DEMO_CLICK_TARGETS = [
  { target: "Get started", secondary: "button" },
  { target: "Sign Up", secondary: "button" },
  { target: "Learn more", secondary: "link" },
  { target: "View Demo", secondary: "button" },
  { target: "Contact Sales", secondary: "button" },
  { target: "Documentation", secondary: "link" },
  { target: "Pricing", secondary: "nav-link" },
];

/**
 * Generate a single demo event with a STABLE unique ID
 * ID is based on monotonic counter, not time - so it never changes once created
 */
function generateSingleDemoEvent(
  eventId: number,
  isSession: boolean,
  timeOffset: number = 0
): any {
  const now = Date.now();
  const country = DEMO_COUNTRIES[randomInt(0, DEMO_COUNTRIES.length - 1)];
  const device = DEMO_BROWSERS[randomInt(0, DEMO_BROWSERS.length - 1)];
  const page = DEMO_PAGES[randomInt(0, DEMO_PAGES.length - 1)];

  if (isSession) {
    return {
      // Stable ID that never changes once event is created
      id: `demo_session_${eventId}`,
      view_id: `view_${10000 + eventId}`,
      event_type: "session",
      time: new Date(now - timeOffset * 1000).toISOString(),
      page: page,
      country: country,
      country_code: country,
      client_os: device.os,
      client_browser: device.browser,
      device_type:
        DEMO_DEVICE_TYPES[randomInt(0, DEMO_DEVICE_TYPES.length - 1)],
      utm_source:
        randomInt(0, 100) < 40
          ? DEMO_UTM_SOURCES[randomInt(0, DEMO_UTM_SOURCES.length - 1)]
          : undefined,
      session_length: randomInt(10, 180),
    };
  } else {
    const clickData =
      DEMO_CLICK_TARGETS[randomInt(0, DEMO_CLICK_TARGETS.length - 1)];
    return {
      id: `demo_metric_${eventId}`,
      event_id: `evt_${100000 + eventId}`,
      time: new Date(now - timeOffset * 1000).toISOString(),
      page: page,
      action: "click",
      target: clickData.target,
      secondary: clickData.secondary,
      x: randomInt(100, 800),
      y: randomInt(100, 600),
      country: country,
      country_code: country,
      client_os: device.os,
      client_browser: device.browser,
      device_type:
        DEMO_DEVICE_TYPES[randomInt(0, DEMO_DEVICE_TYPES.length - 1)],
    };
  }
}

/**
 * Generate demo real-time data
 * Returns realistic live visitor activity for the globe visualization
 *
 * KEY BEHAVIOR:
 * - Caches events with stable IDs that persist between refreshes
 * - Only adds 1-2 new events every 8+ seconds (not every 5s refresh)
 * - Old events are removed after 5 minutes
 * - This prevents the globe from constantly animating "new" events
 */
function generateDemoRealtime() {
  const now = Date.now();

  // If cache is empty or very old (1 min+), initialize with baseline events
  if (
    cachedRealtimeEvents.length === 0 ||
    now - lastRealtimeGeneration > 60000
  ) {
    cachedRealtimeEvents = [];
    eventIdCounter = 0;

    // Generate initial set of 12-15 events spread over last 5 minutes
    const initialCount = randomInt(12, 15);
    for (let i = 0; i < initialCount; i++) {
      const isSession = i < Math.floor(initialCount * 0.7);
      const timeOffset = randomInt(30, 300); // 30s to 5min ago
      const event = generateSingleDemoEvent(
        eventIdCounter++,
        isSession,
        timeOffset
      );
      cachedRealtimeEvents.push(event);
    }
    lastRealtimeGeneration = now;
    lastNewEventTime = now;
  } else {
    // Only add new events if enough time has passed (throttle to ~1-2 per 8-10 seconds)
    const timeSinceLastNewEvent = now - lastNewEventTime;

    if (timeSinceLastNewEvent >= MIN_TIME_BETWEEN_NEW_EVENTS) {
      // Add 1-2 new events
      const newEventCount = randomInt(1, MAX_NEW_EVENTS_PER_INTERVAL);
      for (let i = 0; i < newEventCount; i++) {
        const isSession = _rand() > 0.3;
        const event = generateSingleDemoEvent(
          eventIdCounter++,
          isSession,
          randomInt(0, 10)
        );
        cachedRealtimeEvents.unshift(event); // Add to front (most recent)
      }
      lastNewEventTime = now;
    }

    // Remove old events (older than 5 minutes) and cap total
    const fiveMinutesAgo = now - 5 * 60 * 1000;
    cachedRealtimeEvents = cachedRealtimeEvents
      .filter((e) => new Date(e.time).getTime() > fiveMinutesAgo)
      .slice(0, MAX_REALTIME_EVENTS);

    lastRealtimeGeneration = now;
  }

  // Sort by time (most recent first)
  const liveFeed = [...cachedRealtimeEvents].sort(
    (a, b) => new Date(b.time).getTime() - new Date(a.time).getTime()
  );

  // Calculate active visitors (in last 5 minutes)
  const activeVisitors = liveFeed.filter((event) => {
    const eventTime = new Date(event.time).getTime();
    return now - eventTime < 5 * 60 * 1000;
  }).length;

  return {
    activeVisitors: Math.max(activeVisitors, randomInt(10, 15)),
    liveFeed,
  };
}

/**
 * Generate demo AI analysis
 */
function generateDemoAIAnalysis() {
  return {
    id: "analysis_demo_1",
    status: "completed",
    result: {
      aggregated_insights: {
        optimization_score: 72,
        key_themes: [
          "High engagement on pricing page",
          "Mobile navigation needs improvement",
          "Strong conversion on hero CTA",
          "Cart abandonment is high",
        ],
        quick_wins: [
          "Add exit-intent popup on pricing page",
          "Simplify mobile menu navigation",
          "Test urgent language in hero CTA",
          "Send abandoned cart emails",
        ],
        priority_recommendations: [
          {
            title: "Optimize Mobile Navigation",
            description:
              "Mobile users are bouncing at a 48% higher rate than desktop. Consider implementing a bottom tab bar navigation.",
            priority_score: 95,
            impact_areas: ["Mobile Experience", "Bounce Rate", "Engagement"],
          },
          {
            title: "Reduce Cart Abandonment",
            description:
              "40% of users who add items to cart don't complete checkout. Test trust badges and express checkout options.",
            priority_score: 89,
            impact_areas: ["Conversion Rate", "Revenue", "Checkout Flow"],
          },
          {
            title: "Improve Pricing Page CTA",
            description:
              "The pricing page has high traffic but low conversion. Test more prominent CTA buttons and social proof.",
            priority_score: 82,
            impact_areas: ["Conversion Rate", "Sign Ups", "Revenue"],
          },
        ],
      },
      specialists: [
        {
          specialist: "Conversion Optimizer",
          analysis:
            "Your funnel shows significant drop-off at checkout. The payment step has a 47% abandonment rate.",
          opportunities:
            "Test express checkout, trust badges, and payment option variety to reduce friction.",
          priority_score: 92,
          tests: [
            {
              title: "Express Checkout Button",
              hypothesis:
                "Adding a one-click checkout option will reduce abandonment by 25%",
              control: "Standard multi-step checkout",
              variant: "Express checkout with saved payment methods",
              category: "Checkout Optimization",
              priority_score: 94,
              implementation_complexity: "Medium",
              expectedImpact: "+15-25% conversion rate",
            },
          ],
        },
        {
          specialist: "UX Researcher",
          analysis:
            "Mobile users spend 35% less time on site than desktop users, indicating possible UX issues.",
          opportunities:
            "Simplify mobile navigation, improve touch targets, and optimize page load speed.",
          priority_score: 88,
          tests: [
            {
              title: "Bottom Navigation Bar",
              hypothesis:
                "A persistent bottom nav bar will increase mobile engagement by 30%",
              control: "Hamburger menu",
              variant: "Bottom tab bar with key sections",
              category: "Mobile UX",
              priority_score: 91,
              implementation_complexity: "Low",
              expectedImpact: "+20-30% mobile engagement",
            },
          ],
        },
      ],
    },
    time: daysAgo(2),
    isNew: false,
  };
}

// ============================================================================
// API INTERCEPTOR
// ============================================================================

/**
 * Intercept API calls for demo site and return demo data
 * Usage: const data = await interceptDemoAPI(propertyId, () => actualAPICall(), demoGenerator)
 */
export async function interceptDemoAPI<T>(
  propertyId: string | undefined,
  apiCall: () => Promise<T>,
  demoDataGenerator: () => T
): Promise<T> {
  if (isDemoSite(propertyId)) {
    // Simulate network delay for realism
    await new Promise((resolve) => setTimeout(resolve, randomInt(200, 600)));
    return demoDataGenerator();
  }

  return apiCall();
}

// ============================================================================
// DEMO FUNNEL STORE — localStorage-backed persistence for demo funnels
// ============================================================================

const DEMO_FUNNELS_KEY = 'humblytics:demo:funnels:v1';

function readDemoFunnels(): any[] {
  try {
    const raw = localStorage.getItem(DEMO_FUNNELS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeDemoFunnels(funnels: any[]): void {
  try {
    localStorage.setItem(DEMO_FUNNELS_KEY, JSON.stringify(funnels));
  } catch {
    // localStorage full or unavailable — fail silently
  }
}

export const demoFunnelStore = {
  /** Returns saved funnels merged with the hardcoded demo set (saved funnels take precedence by id). */
  getAll(): any[] {
    const saved = readDemoFunnels();
    const defaults = generateDemoFunnels().funnels;
    // Saved funnels shadow defaults with the same funnel_id
    const savedIds = new Set(saved.map((f: any) => f.funnel_id));
    const unoverridden = defaults.filter((f: any) => !savedIds.has(f.funnel_id));
    return [...saved, ...unoverridden];
  },

  create(funnelData: any): any {
    const saved = readDemoFunnels();
    const newFunnel = {
      ...funnelData,
      funnel_id: `demo_funnel_${Date.now()}`,
      created_at: new Date().toISOString(),
    };
    writeDemoFunnels([...saved, newFunnel]);
    return newFunnel;
  },

  update(funnelId: string, funnelData: any): any {
    const saved = readDemoFunnels();
    // If it's a default funnel being updated for the first time, promote it to saved
    const existing = saved.find((f: any) => f.funnel_id === funnelId);
    if (existing) {
      const updated = saved.map((f: any) =>
        f.funnel_id === funnelId ? { ...f, ...funnelData, funnel_id: funnelId } : f
      );
      writeDemoFunnels(updated);
      return updated.find((f: any) => f.funnel_id === funnelId);
    } else {
      // Promote from defaults
      const defaults = generateDemoFunnels().funnels;
      const base = defaults.find((f: any) => f.funnel_id === funnelId) ?? {};
      const promoted = { ...base, ...funnelData, funnel_id: funnelId };
      writeDemoFunnels([...saved, promoted]);
      return promoted;
    }
  },

  delete(funnelId: string): void {
    const saved = readDemoFunnels();
    writeDemoFunnels(saved.filter((f: any) => f.funnel_id !== funnelId));
  },
};

/**
 * Export all demo generators as a single object for easy access
 */
export const demoGenerators = {
  dashboard: generateDemoDashboardStats,
  pages: generateDemoPagesData,
  clicks: generateDemoClicksData,
  forms: generateDemoFormsData,
  experiments: generateDemoExperiments,
  funnels: () => ({ funnels: demoFunnelStore.getAll() }),
  funnelAnalysis: generateDemoFunnelAnalysis,
  funnelSuggestions: generateDemoFunnelSuggestions,
  heatmaps: generateDemoHeatmaps,
  realtime: generateDemoRealtime,
  aiAnalysis: generateDemoAIAnalysis,
};
